import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  STORYBOARDS_FOLDER,
  getAxiosErorrMessage,
  getUnexpiredToken,
  shuffleArray,
} from "@/utils/utils";
import {
  dequeueElement,
  initializeQueue,
  markElementsWithForce,
  setIsFetching,
  setRegenerateEntireQueue,
  addBulkGeneratedPreview,
  incrementFailureCount,
  addComponentFailure,
  resetFailureCount,
} from "@/store/metadataSlice";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCanvasId } from "@/utils/utils";
import { useSupabase } from "./DatabaseSyncWrapper";
import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import { usePreviewUpdateStore } from "@/store/zustand/previewUpdateStore";
import { JsxElementType } from "@/utils/jsxElementTypes";

interface ElementMetadata {
  hash: string;
  previewUrl?: string;
  error?: boolean;
  props?: object; // the props passed into the component for the preview ie (children, className, etc)
  state?: object; //not used for now
}

interface ElementMetadataContextType {
  metadata: Record<string, ElementMetadata>;
  ungeneratedCount: number;
  ungeneratedElements: string[];
}

const ElementMetadataContext = createContext<ElementMetadataContextType>({
  metadata: {},
  ungeneratedCount: 0,
  ungeneratedElements: [],
});

interface Props {
  children: React.ReactNode;
  framework?: string;
}

interface RootState {
  metadata: {
    elementsToFetch: string[];
    regenerateEntireQueue: boolean;
    isFetching: boolean;
    processingInformation: Record<string, { force?: boolean }>;
    queueFailureState: {
      hasFailures: boolean;
      consecutiveFailures: number;
      lastFailureMessage?: string;
      failedAt?: number;
    };
  };
  supabaseStore: {
    elementMetadata: Record<string, ElementMetadata>;
    elementMetadataLoadStatus: string;
  };
}

export const ElementMetadataProvider = React.memo(
  ({ children, framework }: Props) => {
    const [metadata, setMetadata] = useState<Record<string, ElementMetadata>>(
      {}
    );
    const dispatch = useDispatch();

    const canvasId = useCanvasId();

    const treeElementLookup = useCodebaseStore(
      (state) => state.treeElementLookup
    );
    const projectLaunchStatus = useCodebaseStore(
      (state) => state.projectLaunchStatus
    );

    const elementsToFetch = useSelector(
      (state: RootState) => state.metadata.elementsToFetch
    );
    const regenerateEntireQueue = useSelector(
      (state: RootState) => state.metadata.regenerateEntireQueue
    );

    const { triggerRefetchElementMetadata } = useSupabase();

    const { session } = useSession();
    const processingInformation = useSelector(
      (state: RootState) => state.metadata.processingInformation
    );

    const projectElementMetadata = useSelector((state: RootState) => {
      return state.supabaseStore.elementMetadata;
    });
    const projectElementMetadataLoaded =
      useSelector((state: RootState) => {
        return state.supabaseStore.elementMetadataLoadStatus === "succeeded";
      }) && canvasId;

    const queueFailureState = useSelector(
      (state: RootState) => state.metadata.queueFailureState
    );

    const hashToElementMetadata = useMemo(() => {
      const newMap: Record<string, ElementMetadata> = {};
      if (projectElementMetadata) {
        for (const elementMetadata of Object.values(projectElementMetadata)) {
          newMap[elementMetadata.hash] = elementMetadata;
        }
      }
      return newMap;
    }, [projectElementMetadata]);

    const validElementsCodebaseIds = useMemo(() => {
      if (projectLaunchStatus !== "succeeded") return [];

      return Object.keys(treeElementLookup)?.filter((codebaseId: string) => {
        const element = treeElementLookup[codebaseId];
        const filenameWithoutExtension = element.filename.replace(
          /\.[^/.]+$/,
          ""
        );

        // Only allow stories
        if (element.type !== "storybook-component") {
          return false;
        }
        // Exclude libraries & templates
        if (element.library || element.isTemplateStory) {
          return false;
        }

        // Exclude Next JS app file
        if (
          (framework === "NEXT_JS" || framework === "REMIX") &&
          filenameWithoutExtension.endsWith("pages/_app")
        ) {
          return false;
        }

        // No unsaved components
        if (element.filename.includes(STORYBOARDS_FOLDER)) {
          return false;
        }

        return true;
      });
    }, [framework, projectLaunchStatus, treeElementLookup]);

    const validUngeneratedElements = useMemo(() => {
      if (!validElementsCodebaseIds?.length) return [];
      if (!projectElementMetadataLoaded) return [];

      const filtered = validElementsCodebaseIds?.filter(
        (codebaseId: string) => {
          const element = treeElementLookup[codebaseId];
          const fileHash = element?.fileHash;

          if (!fileHash) {
            return false;
          }

          if (element.type !== JsxElementType.StorybookComponent) {
            return false;
          }

          // Already generated
          if (hashToElementMetadata[fileHash]) {
            return false;
          }

          // Let useImagePreview handle preview existence checking
          // Just include all elements that don't have metadata
          return true;
        }
      );

      // Group by component and prioritize first variants first
      const componentGroups: { [filename: string]: string[] } = {};

      // Group elements by their filename (component file)
      for (const codebaseId of filtered) {
        const element = treeElementLookup[codebaseId];
        const filename = element?.filename;
        if (filename) {
          if (!componentGroups[filename]) {
            componentGroups[filename] = [];
          }
          componentGroups[filename].push(codebaseId);
        }
      }

      const firstVariants: string[] = [];
      const remainingVariants: string[] = [];

      // For each component group, pick the first variant and add rest to remaining
      for (const group of Object.values(componentGroups)) {
        if (group.length === 0) continue;

        // First element goes to first variants, rest go to remaining
        firstVariants.push(group[0]);
        remainingVariants.push(...group.slice(1));
      }

      // Return first variants first, then remaining variants
      return [...firstVariants, ...remainingVariants];
    }, [
      validElementsCodebaseIds,
      treeElementLookup,
      projectElementMetadataLoaded,
      hashToElementMetadata,
    ]);

    useEffect(() => {
      if (!projectElementMetadata) {
        return;
      }

      setMetadata((prev) => ({ ...prev, ...projectElementMetadata }));
    }, [projectElementMetadata]);

    const [processedCount, setProcessedCount] = useState(0);
    const isFetching = useSelector(
      (state: RootState) => state.metadata.isFetching
    );

    const { notifyPreviewUpdate } = usePreviewUpdateStore((state) => ({
      notifyPreviewUpdate: state.notifyPreviewUpdate,
    }));

    const fetchMetadata = useCallback(
      async (codebaseId: string) => {
        if (!session || !canvasId) {
          return;
        }

        dispatch(setIsFetching(true));
        const element = treeElementLookup[codebaseId];
        const elementProcessingInfo = processingInformation[codebaseId];

        try {
          const supabaseJWT = await getUnexpiredToken(session);
          const response = await axios.post(
            `${
              import.meta.env.VITE_BACKEND_BASE_URL
            }/canvases/${canvasId}/generateMetadata`,
            {
              treeElement: element,
              force: elementProcessingInfo?.force,
            },
            {
              headers: {
                Authorization: `Bearer ${supabaseJWT}`,
              },
              timeout: 120000,
            }
          );

          // Reset consecutive failures on success
          dispatch(resetFailureCount());

          // If successful generation, immediately update local metadata and trigger preview update
          if (
            response.data &&
            response.data.type === "success" &&
            response.data.previewUrl
          ) {
            // Immediately update local metadata to show preview without waiting for DB refetch
            if (element.fileHash) {
              setMetadata((prev) => ({
                ...prev,
                [element.fileHash as string]: {
                  hash: element.fileHash as string,
                  previewUrl: response.data.previewUrl,
                  props: response.data.props,
                  state: response.data.state,
                },
              }));
            }

            // If we're in bulk generation mode, also add to bulk generated list
            if (element.fileHash && element.componentName) {
              dispatch(
                addBulkGeneratedPreview({
                  codebaseId: element.codebaseId,
                  fileHash: element.fileHash,
                  previewUrl: response.data.previewUrl,
                  componentName: element.componentName,
                  filename: element.filename,
                  generatedAt: Date.now(),
                })
              );
            }

            // Use fileHash as the component key since that's what useImagePreview uses
            notifyPreviewUpdate(element.fileHash);
          }
        } catch (error) {
          console.error("Failed to generate metadata:", error);
          if (axios.isAxiosError(error)) {
            console.error("API Error details:", {
              status: error.response?.status,
              data: error.response?.data,
            });
          }

          // Increment consecutive failures
          const errorMessage = axios.isAxiosError(error)
            ? getAxiosErorrMessage(error)
            : JSON.stringify(error);
          dispatch(incrementFailureCount(errorMessage));

          dispatch(
            addComponentFailure({
              codebaseId: element.codebaseId,
              componentName: element.componentName || element.filename,
              filename: element.filename,
              errorMessage,
              failedAt: Date.now(),
            })
          );
        } finally {
          dispatch(setIsFetching(false));
          dispatch(dequeueElement());
          setProcessedCount((prev) => {
            const newCount = prev + 1;
            if (newCount >= 10) {
              triggerRefetchElementMetadata?.();
              return 0;
            }
            return newCount;
          });
        }
      },
      [
        dispatch,
        canvasId,
        processingInformation,
        session,
        treeElementLookup,
        triggerRefetchElementMetadata,
        notifyPreviewUpdate,
      ]
    );

    // Effect to process queue
    useEffect(() => {
      if (isFetching || !elementsToFetch.length) {
        if (processedCount > 0 && !elementsToFetch.length) {
          triggerRefetchElementMetadata?.();
          setProcessedCount(0);
        }
        return;
      }

      // Stop processing if we've had 10 consecutive failures

      if (queueFailureState.consecutiveFailures >= 10) {
        console.log("Too many consecutive failures, stopping queue processing");
        dispatch(resetFailureCount()); // Reset for next batch
        return;
      }

      fetchMetadata(elementsToFetch[0]);
    }, [
      dispatch,
      elementsToFetch,
      fetchMetadata,
      isFetching,
      processedCount,
      queueFailureState.consecutiveFailures,
      triggerRefetchElementMetadata,
    ]);

    useEffect(() => {
      if (regenerateEntireQueue) {
        dispatch(initializeQueue(shuffleArray(validElementsCodebaseIds)));
        dispatch(markElementsWithForce(validElementsCodebaseIds));
        dispatch(setRegenerateEntireQueue(false));
      }
    }, [dispatch, regenerateEntireQueue, validElementsCodebaseIds]);

    const contextValue = useMemo(
      () => ({
        metadata,
        ungeneratedCount: validUngeneratedElements.length,
        ungeneratedElements: validUngeneratedElements,
      }),
      [metadata, validUngeneratedElements]
    );

    return (
      <ElementMetadataContext.Provider value={contextValue}>
        {children}
      </ElementMetadataContext.Provider>
    );
  }
);

export const useElementMetadata = (): ElementMetadataContextType => {
  const context = useContext(ElementMetadataContext);
  if (!context) {
    throw new Error(
      "useElementMetadata must be used within an ElementMetadataProvider"
    );
  }
  return context;
};
